import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// import Auth from '@/models/Auth';
import clock from '@/lib/clock';
import hash from '@/lib/helpers/hash';
import toCurrency from '@/lib/helpers/toCurrency';
import toPercentage from '@/lib/helpers/toPercentage';
import toDate from '@/lib/helpers/toDate';
import toDatetime from '@/lib/helpers/toDatetime';
import toNumber from '@/lib/helpers/toNumber';
import getTableRowValue from '@/lib/helpers/getTableRowValue';
import getImage from '@/lib/helpers/getImage';
import i18n from '@/lib/i18n';
import merge from 'lodash/merge';

/**
 * Create a new jsPDF object
 * @param {Object} options @see http://raw.githack.com/MrRio/jsPDF/master/docs/jsPDF.html#setDisplayMode
 * @returns jsPDF
 */
export const createPdf = (options, tableOptions = null) => {
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: [960, 540],
        putOnlyUsedFonts: true,
        compress: true,
        precision: 16,
        userUnit: 1.0,
        hotfixes: ['px_scaling'],
        encryption: {
            userPassword: null,
            ownerPassword: null,
            userPermissions: ['print', 'modify', 'copy', 'annot-forms']
        },
        floatPrecision: 16,
        ...options
    });

    doc.positionX = 0;
    doc.positionY = 85;
    doc.$_lastParagraphHeight = 0;
    doc.$_lastParagraphWidth = 0;
    doc.$_column = null;
    doc.$_pageMarginRight = 30;
    doc.$_pageMarginRight = 30;
    // doc.$_colours = Auth().branding().colours;
    doc.$_colours = {
        primary: '#5abdf9',
        primaryBackground: '#5ABDF9',
        primaryLight: '#DFEAF1',
        primaryText: '#0670B1'
    };
    doc.$_hasCover = false;

    doc.$getColour = name => doc.$_colours[`${name}`];

    doc.$getLineHeight = () => {
        return Math.ceil(doc.getLineHeight() + 4 / 3);
    };

    doc.$resetX = (padding = true) => {
        if (doc.$_column) {
            doc.positionX = doc.$_column.x;
        } else if (padding) {
            doc.positionX = doc.$_pageMarginRight;
        } else {
            doc.positionX = 0;
        }
    };

    doc.$resetY = () => {
        if (doc.$_column) {
            doc.positionY = doc.$_column.y;
        } else {
            doc.positionY = 85;
        }
    };

    doc.$newLine = (quantity = 1, lineHeight = null) => {
        doc.$resetX();
        lineHeight = lineHeight || doc.$getLineHeight();
        doc.positionY += lineHeight * quantity;
    };

    doc.$removeLine = (quantity = 1, lineHeight = null) => {
        doc.$resetX();
        lineHeight = lineHeight || doc.$getLineHeight();
        doc.positionY -= lineHeight * quantity;
    };

    doc.$divider = () => {
        doc.$resetX();
        doc.setFillColor('#eee');
        doc.rect(doc.positionX, doc.positionY, doc.$getWidth(), 1, 'F');
    };

    doc.$createColumns = (options = {}) => {
        options = {
            height: 0,
            width: doc.internal.pageSize.getWidth(),
            padding: { l: doc.$_pageMarginRight, r: doc.$_pageMarginRight },
            gutter: 30,
            columns: 2,
            ...options
        };

        const y = doc.positionY;

        const paddingWidth = options.padding.l + options.padding.r;
        const gutterWidth = options.gutter * (options.columns - 1);
        const colWidth = (options.width - paddingWidth - gutterWidth) / options.columns;

        const parts = [];

        for (let i = 0; i < options.columns; i++) {
            let x = options.padding.l;

            if (i > 0) {
                x += (colWidth + options.gutter) * i;
            }

            parts.push({ x, y, width: colWidth, height: options.height, right: x + colWidth });
        }

        return parts;
    };

    doc.$setColumn = (column, resetPositionY = true) => {
        doc.$_column = column;

        if (column) {
            doc.positionX = column.x;
            if (resetPositionY) {
                doc.positionY = column.y;
            }
        }
    };

    doc.$getWidth = () => {
        if (doc.$_column) {
            return doc.$_column.width;
        }

        return doc.internal.pageSize.getWidth() - (doc.$_pageMarginRight + doc.$_pageMarginRight);
    };

    if (tableOptions) {
        doc.autoTable(tableOptions);
    }

    return doc;
};

export const download = (doc, name) => {
    name = name ? name : clock().format('YYYY-MM-DD');
    doc.save(`${name}.pdf`);
};

export const output = doc => {
    return doc.output('datauristring');
};

export const createCover = async (doc = null, heading = null, text = null) => {
    doc = doc ? doc : createPdf();

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const isPortrait = pageHeight > pageWidth;

    // Add the background image
    if (isPortrait) {
        const backgroundImage = await getImage(require('@/images/pdf/cover-portrait.jpg'));
        const backgroundImageWidth = backgroundImage.width + 1;
        const backgroundImageHeight = backgroundImage.height + 1;
        doc.addImage(backgroundImage, 'jpg', 0, 0, backgroundImageWidth, backgroundImageHeight);
    } else {
        const backgroundImage = await getImage(require('@/images/pdf/cover-landscape.jpg'));
        const backgroundImageWidth = backgroundImage.width;
        const backgroundImageHeight = backgroundImage.height;
        doc.addImage(
            backgroundImage,
            'jpg',
            pageWidth - backgroundImageWidth + 1,
            pageHeight - backgroundImageHeight + 1,
            backgroundImageWidth,
            backgroundImageHeight
        );
    }

    // Add the white box overlay and logo

    if (isPortrait) {
        const rectangleHeight = pageWidth / 8;

        doc.setFillColor('#ffffff');
        doc.rect(0, 0, pageWidth, rectangleHeight, 'F');
        doc.triangle(0, rectangleHeight, pageWidth, rectangleHeight, 0, rectangleHeight * 2, 'F');

        const logoImage = await getImage(require('@/images/pdf/logo.png'));
        const logoImageHeight = rectangleHeight - 30;
        const logoImageWidth = (logoImage.width / logoImage.height) * logoImageHeight;
        doc.addImage(logoImage, 'png', doc.$_pageMarginRight, 45, logoImageWidth, logoImageHeight);
    } else {
        const rectangleWidth = pageWidth / 4;

        doc.setFillColor('#ffffff');
        doc.rect(0, 0, rectangleWidth + 1, pageHeight, 'F');
        doc.triangle(rectangleWidth, 0, (pageWidth / 3) * 2, pageHeight, rectangleWidth, pageHeight, 'F');

        const logoImage = await getImage(require('@/images/pdf/logo.png'));
        const logoImageWidth = rectangleWidth - 30;
        const logoImageHeight = (logoImage.height / logoImage.width) * logoImageWidth;
        doc.addImage(logoImage, 'png', doc.$_pageMarginRight, 50, logoImageWidth, logoImageHeight);
    }

    // Add the heading text
    if (heading) {
        doc.setFontSize(20);
        doc.setFont(undefined, 'bold');
        doc.setTextColor('#ffffff');

        const headingBottomOffset = isPortrait ? 100 : 160;

        const textY = pageHeight - headingBottomOffset;

        const textSize = doc.getTextDimensions(heading);

        let textBgWidth = textSize.w + 60;

        if (textBgWidth < (pageWidth / 3) * 2) {
            textBgWidth = (pageWidth / 3) * 2;
        }

        let textBgHeight = textSize.h + 30;

        // Add the heading background
        doc.setFillColor(doc.$getColour('primaryBackground'));
        doc.rect(0, textY - textSize.h - 13, textBgWidth, textBgHeight, 'F');

        doc.text(heading, doc.$_pageMarginRight, textY);
    }

    if (text) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor('#394350');

        const textBottomOffset = 25;

        const textY = pageHeight - textBottomOffset;
        doc.text(text, doc.$_pageMarginRight, textY);
    }

    // Reset the font
    doc.setFontSize(16);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(doc.$getColour('primaryText'));

    doc.$_hasCover = true;

    return doc;
};

export const createBackCover = async doc => {
    doc = await createPage(doc);
    doc.positionX = 0;
    doc.positionY = 0;

    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();
    doc.positionY = height / 2;

    doc.setFillColor(doc.$getColour('primaryBackground'));
    doc.rect(0, 0, width, height, 'F');

    doc = await createParagraph(doc, 'enquiries@growthinvest.com', {
        size: 10,
        align: 'center',
        colour: '#fff',
        baseline: 'middle'
    });
    doc = await createParagraph(doc, '0300 303 0037', {
        size: 10,
        align: 'center',
        colour: '#fff',
        baseline: 'middle'
    });

    doc.positionY = height - 70;

    doc = await createParagraph(doc, 'Warnford Court, 29 Throgmorton Street, London EC2N 2AT', {
        size: 10,
        align: 'center',
        colour: '#fff'
    });

    doc.$newLine();

    doc = await createParagraph(
        doc,
        // eslint-disable-next-line max-len
        'GrowthInvest Platform is a trading name of EIS Platforms Limited. EIS Platforms Limited (FRN: 694945) is an appointed representative  of Sapphire Capital Partners LLP (FRN:565716) which is authorised and regulated by the Financial Conduct Authority in the UK.',
        {
            size: 8,
            align: 'center',
            colour: '#fff',
            padding: { r: 100, l: 100 }
        }
    );

    return doc;
};

export const createPage = (doc = null, heading = null) => {
    doc = doc ? doc : createPdf();

    doc.$setColumn();

    doc.addPage();

    doc.$_pageMarginRight = 30;
    doc.$_pageMarginRight = 30;

    doc.$resetX();
    doc.$resetY();

    if (heading) {
        doc = createHeading(doc, heading);
    }

    return doc;
};

export const createHeading = (doc, text, size = 16, colour = null, options = {}) => {
    if (!text) {
        return doc;
    }

    options = {
        align: 'left',
        baseline: 'top',
        charSpace: 0,
        maxWidth: 0,
        style: 'normal',
        ...options
    };

    doc.$resetX();

    doc.setFontSize(size ? size : 16);
    doc.setFont(undefined, options.style);
    doc.setTextColor(colour ? colour : doc.$getColour('primaryText'));

    const textSize = doc.getTextDimensions(text);

    doc.text(text, doc.positionX, doc.positionY, options);

    doc.positionY += textSize.h;
    doc.positionX += textSize.w;

    return doc;
};

export const createParagraph = (doc, text = null, options = {}) => {
    if ('padding' in options) {
        options.padding = {
            t: 't' in options.padding ? options.padding.t : 0,
            r: 'r' in options.padding ? options.padding.r : 0,
            b: 'b' in options.padding ? options.padding.b : 0,
            l: 'l' in options.padding ? options.padding.l : 0
        };
    }

    options = {
        size: 11,
        align: 'left',
        baseline: 'top',
        colour: '#394350',
        weight: 'normal',
        width: doc.$getWidth(),
        padding: { t: 0, r: 0, b: 0, l: 0 },
        prefix: null,
        prefixWidth: 0,
        prefixPadding: { r: 0, l: 0 },
        ...options
    };

    if (options.padding.t) {
        doc.positionY += options.padding.t;
    }

    if (options.prefix && !options.prefixWidth) {
        options.prefixWidth = doc.getTextWidth(options.prefix) + options.prefixPadding.r + options.prefixPadding.l;
    }

    doc.setFontSize(options.size);
    doc.setFont(undefined, options.weight);
    doc.setTextColor(options.colour);

    if (options.prefix) {
        doc.$resetX();
        doc.positionX += options.padding.l + options.prefixPadding.l;
        doc.text(options.prefix, doc.positionX, doc.positionY, { baseline: 'top' });
    }

    const width = options.width - options.prefixWidth - options.padding.l - options.padding.r;
    const lineHeight = doc.$getLineHeight();
    const textWidth = doc.getTextWidth(text);
    const textSplit = doc.splitTextToSize(text, width);

    doc.$_lastParagraphHeight = 0;
    doc.$_lastParagraphWidth = 0;

    for (let i = 0; i < textSplit.length; i++) {
        doc.$resetX();
        doc.positionX += options.padding.l + options.prefixWidth;

        if (options.align === 'center') {
            doc.positionX = options.width / 2 + doc.$_pageMarginRight;
        }

        doc.text(textSplit[`${i}`], doc.positionX, doc.positionY, {
            baseline: options.baseline,
            align: options.align
        });

        if (i === textSplit.length - 1) {
            doc.positionY += lineHeight;
            doc.$_lastParagraphHeight += lineHeight;
        } else {
            doc.positionY += lineHeight;
        }

        doc.positionX += textWidth;
        doc.$_lastParagraphWidth += textWidth;
    }

    if (options.padding.b) {
        doc.positionY += options.padding.b;
    }

    return doc;
};

export const createInput = async (doc, options = {}) => {
    options = {
        label: null,
        labelColour: doc.$getColour('primaryText'),
        hint: null,
        hintColour: '#9a9a9a',
        value: null,
        valueColour: '#5a5a5a',
        height: 20,
        minHeight: 0,
        padding: { t: 5, r: 0, b: 1, l: 5 },
        width: doc.$getWidth(),
        ...options
    };

    if (options.height === 'auto') {
        let height = 20;

        doc.setFontSize(10);
        if (options.value && Array.isArray(options.value)) {
            height = options.value.length * doc.$getLineHeight();
            height += options.padding.t;
        }

        options.height = height;
    }

    options.height = options.height < options.minHeight ? options.minHeight : options.height;

    doc.setFontSize(8);
    const lineHeight = doc.$getLineHeight();

    doc.$resetX();
    doc.positionY += 10;

    if (options.label) {
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(options.labelColour);
        doc.text(options.label, doc.positionX, doc.positionY, {
            baseline: 'top'
        });
        doc.positionY += lineHeight;
        doc.$resetX();
    }

    if (options.hint) {
        doc = await createParagraph(doc, options.hint, {
            size: 6,
            colour: options.hintColour,
            weight: 'normal'
        });
        doc.$resetX();
    }

    doc.setFillColor('#f9f9f9');
    doc.setDrawColor(doc.$getColour('primaryLight'));
    doc.rect(doc.positionX, doc.positionY, options.width, options.height, 'FD');

    doc.positionY += options.height;
    const endY = doc.positionY;

    if (options.value) {
        doc.positionY -= options.height;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(options.valueColour);

        doc.positionY += options.padding.t;
        doc.positionX += options.padding.l;

        if (Array.isArray(options.value)) {
            for (const lineValue of options.value) {
                doc.text(lineValue, doc.positionX, doc.positionY, {
                    baseline: 'top'
                });

                doc.positionY += lineHeight + 1;
            }
        } else if (typeof options.value === 'string') {
            doc.text(options.value, doc.positionX, doc.positionY, {
                baseline: 'top'
            });

            doc.positionY += lineHeight;
        }
        doc.$resetX();
    }

    doc.positionY = endY;

    return doc;
};

export const createHtml = (doc, html = null) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageCount = doc.internal.getNumberOfPages();

    doc.$resetX();
    // Manually set the Y offset to the top of the current page
    doc.positionY = (pageCount - 1) * pageHeight + 130;

    const options = {
        // @see http://raw.githack.com/MrRio/jsPDF/master/docs/module-html.html#~html
        x: doc.positionX,
        y: doc.positionY,
        autoPaging: 'text',
        width: pageWidth - 60,
        windowWidth: (pageWidth - 60) * 2,
        html2canvas: {
            backgroundColor: null,
            foreignObjectRendering: false,
            enableLinks: false
        }
    };

    doc = doc.html(html, options);

    return doc;
};

export const addPageHeaders = async (doc, skipCover = true) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const isPortrait = pageHeight > pageWidth;

    const logoImage = await getImage(require('@/images/pdf/logo.png'));
    const logoImageWidth = isPortrait ? pageWidth / 5 : pageWidth / 10;
    const logoImageHeight = (logoImage.height / logoImage.width) * logoImageWidth;

    const pageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
        if (doc.$_hasCover && skipCover && i === 1) continue;
        doc.setPage(i);
        doc.addImage(logoImage, 'png', doc.$_pageMarginRight, 30, logoImageWidth, logoImageHeight);
    }

    return doc;
};

export const addPageNumbers = async (doc, skipCover = true) => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor('#999999');

    const startAt2 = doc.$_hasCover && skipCover;

    for (let i = 1; i <= pageCount; i++) {
        if (startAt2 && i === 1) {
            continue;
        }

        doc.setPage(i);
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();

        const pageNumber = startAt2 ? i - 1 : i;
        const pageTotal = startAt2 ? pageCount - 1 : pageCount;

        doc.text(`Page ${pageNumber} of ${pageTotal}`, pageWidth - doc.$_pageMarginRight, 30, {
            baseline: 'bottom',
            align: 'right'
        });
    }

    return doc;
};

export const addFootnotes = async (doc, notes = []) => {
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor('#999999');

    const maxSymbolLength = Math.max(...notes.map(n => n.symbol.length));

    doc.$newLine(2);
    for (const note of notes) {
        doc = await createParagraph(doc, note.text, {
            size: 8,
            prefix: note.symbol.padEnd(maxSymbolLength, ' '),
            prefixPadding: { l: 0, r: 4 }
        });
    }

    return doc;
};

/**
 * Create a new table with the given options
 * @param {Object} tableOptions @see https://github.com/simonbengtsson/jsPDF-AutoTable
 * @param {jsPDF|null} doc
 * @returns jsPDF
 */
export const createTable = async (tableOptions = {}, doc = null) => {
    doc = doc ? doc : createPdf();
    doc.$resetX();

    let startY = doc.positionY;
    let margin = { top: 85, right: 30, bottom: 20, left: 30 };

    if ('heading' in tableOptions) {
        const headingSize = 'headingSize' in tableOptions ? tableOptions.headingSize : null;

        let headingHeight = doc.positionY;
        doc = createHeading(doc, tableOptions.heading, headingSize);
        headingHeight = doc.positionY - headingHeight;

        startY += headingHeight + 3;
    }

    const headerLength =
        'head' in tableOptions && Array.isArray(tableOptions.head) && Array.isArray(tableOptions.head[0])
            ? tableOptions.head[0].length
            : null;

    let cellWidth = 'wrap';

    if (headerLength) {
        cellWidth = Math.floor(doc.$getWidth() / headerLength);
    }

    doc.autoTable({
        theme: 'plain',
        styles: {
            cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
            cellWidth,
            fillColor: false,
            font: 'helvetica',
            fontSize: 7,
            fontStyle: 'normal',
            halign: 'left',
            minCellHeight: 0,
            minCellWidth: 10,
            overflow: 'linebreak'
        },
        headStyles: {
            fillColor: doc.$getColour('primaryText'),
            fontStyle: 'bold',
            textColor: doc.$getColour('primaryLight'),
            valign: 'bottom'
        },
        bodyStyles: {
            textColor: '#454545',
            valign: 'middle',
            fillColor: '#f9f9f9'
        },
        footStyles: {},
        alternateRowStyles: {
            fillColor: '#ffffff'
        },
        columnStyles: {},
        startY: startY,
        margin: margin,
        pageBreak: 'auto',
        rowPageBreak: 'avoid',
        tableWidth: 'auto',
        showHead: 'everyPage',
        showFoot: 'everyPage',
        tableLineWidth: 0,
        tableLineColor: false,
        horizontalPageBreak: false, // Set to true to allow tables to overflow onto a new page
        // horizontalPageBreakRepeat: 'name',
        ...tableOptions
    });

    doc.positionY = doc.lastAutoTable.finalY;

    return doc;
};

export const toTable = async (head, columns, body, name = null, doc = null) => {
    let fileName = name ? name : hash({ head, columns }, 8);
    fileName = `${clock().format('YYYY-MM-DD')}_${fileName}`;

    doc = await createTable(
        {
            head: [head],
            columns,
            body,
            heading: name ? name : 'Export',
            horizontalPageBreak: true
        },
        doc
    );

    doc = await addPageHeaders(doc, false);

    download(doc, fileName);
};

export const toCell = (header, content = null, options = {}) => {
    if (!header) {
        return null;
    }

    let cell = {
        content: null,
        rowSpan: null,
        colSpan: null,
        styles: {},
        header: null
    };

    if (typeof header === 'object' && 'text' in header) {
        let width = 'wrap';

        if ('width' in header) {
            if (header.width.includes('rem')) {
                width = parseFloat(header.width.replace('rem', '')) * 6.3;
            } else if (header.width.includes('%')) {
                width = parseFloat(header.width.replace('%', '')) * 5;
            }
        }

        cell.content = [null, undefined].includes(content) ? '-' : content;
        // cell.styles.cellWidth = width;
        cell.styles.minCellWidth = width;
        cell.styles.halign = header.align;
        cell.header = header;
    }

    cell = merge(cell, options);

    return cell;
};

export const convertHeadersToHead = headers => {
    const head = [];

    for (const header of headers) {
        head.push(toCell(header, header.text));
    }

    return head;
};

export const convertRowsToBody = (rows, head) => {
    return rows.map(row => {
        return head.reduce((acc, cell) => {
            const header = cell.header;
            const item = row;
            const rawValue = getTableRowValue({ header, item, value: row[`${header.value}`] });

            const options = {};

            let classList = 'classList' in header ? header.classList || [] : [];

            if (typeof classList === 'function') {
                classList = header.classList({ item }) || [];
            }

            if (classList.includes('error--text')) {
                options.styles = {
                    textColor: '#bf0025'
                };
            }

            let value = rawValue;

            if (header.format === 'money') {
                if (value && 'amount' in value && 'currency' in value) {
                    value = toCurrency(value.amount, value.currency, 2);
                } else {
                    value = null;
                }
            }

            if (header.format === 'percentage') {
                value = toPercentage(value, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                });
            }

            if (header.format === 'date' && value) {
                value = toDate(value);
            }

            if (header.format === 'datetime' && value) {
                value = toDatetime(value);
            }

            if (header.format === 'number') {
                value = toNumber(value, header.formatOptions);
            }

            if (header.enum && header.enumA11yKey && value) {
                value = i18n.t(`${header.enumA11yKey}.${value}`);
            }

            if (header.append && value) {
                value = `${value} ${header.append}`;
            }

            acc[`${header.value}`] = toCell(header, value, options);
            return acc;
        }, {});
    });
};
